import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DateOnly } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import { amcVisitStatusMap } from "features/AmcVisitLogs/constants/amc-visit.constants";
import { useAmcVisitOverview } from "./AmcVisitOverviewContext";
import OverviewTab from "./OverviewTab";
import {
  AmcVisitOverviewTabParam,
  amcVisitOverviewTabIdFromSearchParam,
  isValidAmcVisitOverviewTabParam,
  searchParamForAmcVisitOverviewTabId,
} from "./overviewTabSearchParams";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { shouldShowAmcVisitAssignmentActions } from "features/AmcVisitLogs/utils/amcVisitActionPolicy";
import {
  startAmcVisit,
  completeAmcVisit,
} from "features/AmcVisitLogs/services/AmcVisitService";
import { AmcVisitStatus } from "features/AmcVisitLogs/constants/amc-visit.constants";
import CompleteVisitModal from "features/AmcVisitLogs/CompleteVisitModal";
import type { CompleteAmcVisitRequest } from "features/AmcVisitLogs/types/amc-visit.types";

const Section = () => {
  const { can } = useAccess();
  const canEditVisit = can(Permissions.AMC_VISIT.UPDATE);
  const canCompleteVisit = can(Permissions.AMC_VISIT.COMPLETE);
  const [searchParams, setSearchParams] = useSearchParams();
  const { visit, refetch } = useAmcVisitOverview();
  const [startingVisit, setStartingVisit] = useState(false);
  const [completeModal, setCompleteModal] = useState(false);

  const handleStartVisit = useCallback(async () => {
    if (!visit) return;
    setStartingVisit(true);
    try {
      const res = await startAmcVisit(visit.id);
      if (res.success) {
        toast.success(res.message ?? "Visit started.");
        await refetch();
      }
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not start visit",
      );
    } finally {
      setStartingVisit(false);
    }
  }, [visit, refetch]);

  const handleComplete = useCallback(
    async (body: CompleteAmcVisitRequest) => {
      if (!visit) return;
      try {
        const res = await completeAmcVisit(visit.id, body);
        if (res.success) {
          toast.success(res.message ?? "Visit completed.");
          setCompleteModal(false);
          await refetch();
        }
      } catch (err: unknown) {
        toast.error(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ?? "Could not complete visit",
        );
      }
    },
    [visit, refetch],
  );

  const tabQuery = searchParams.get("tab");

  useEffect(() => {
    if (tabQuery && !isValidAmcVisitOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  if (!visit) {
    return (
      <Row>
        <Col lg={12}>
          <p className="text-muted mb-0">This visit could not be loaded.</p>
        </Col>
      </Row>
    );
  }

  const activeTab = amcVisitOverviewTabIdFromSearchParam(tabQuery);

  const setTabInUrl = (tabId: string) => {
    const param = searchParamForAmcVisitOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!param || param === AmcVisitOverviewTabParam.OVERVIEW) {
          next.delete("tab");
        } else {
          next.set("tab", param);
        }
        return next;
      },
      { replace: true },
    );
  };

  const editPath = `/amc-management/visit-logs/edit/${visit.id}`;
  const hideHeaderActions = !shouldShowAmcVisitAssignmentActions(visit.status);

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <div>
                      <h4 className="fw-bold">{visit.code}</h4>
                      <div className="hstack gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                          <span className="text-muted">Status:</span>
                          <StatusBadge
                            value={visit.status}
                            map={amcVisitStatusMap}
                          />
                        </div>
                        {visit.scheduledAt ? (
                          <>
                            <div className="vr" />
                            <div>
                              Scheduled:{" "}
                              <span className="fw-medium">
                                <DateOnly date={visit.scheduledAt} />
                              </span>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {!hideHeaderActions ? (
                    <div className="col-md-auto">
                      <div className="hstack gap-2 flex-wrap">
                        {canEditVisit &&
                        visit.status === AmcVisitStatus.PLANNED ? (
                          <Button
                            type="button"
                            color="success"
                            size="sm"
                            disabled={startingVisit}
                            onClick={() => void handleStartVisit()}
                          >
                            {startingVisit ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                  aria-hidden
                                />
                                Starting…
                              </>
                            ) : (
                              <>
                                <i className="ri-play-line me-1" />
                                Start visit
                              </>
                            )}
                          </Button>
                        ) : null}
                        {canCompleteVisit &&
                        visit.status === AmcVisitStatus.IN_PROGRESS ? (
                          <Button
                            type="button"
                            color="success"
                            size="sm"
                            onClick={() => setCompleteModal(true)}
                          >
                            <i className="ri-check-double-line me-1" />
                            Complete visit
                          </Button>
                        ) : null}
                        {canEditVisit ? (
                          <Link
                            to={editPath}
                            className="btn btn-light btn-sm border"
                          >
                            <i className="ri-pencil-line align-bottom me-1" />
                            Edit
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </Row>

                <Nav className="nav-tabs-custom border-bottom-0" role="tablist">
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>
      <CompleteVisitModal
        show={completeModal}
        onCompleteClick={handleComplete}
        onCloseClick={() => setCompleteModal(false)}
        record={visit}
      />
      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
